/* eslint-disable */
import React, { useEffect, useRef, useState, useCallback, useMemo } from 'react';
import { usePostPreSignedUrl, useUpdateMenusInfo } from 'hooks';
import { IImageState, IMenuDetailData, menuStatusItems, TImageType, TMenuStatus } from './MenuManageSchema';
import axios from 'axios';
import { Button, Form, Input, Select, notification } from 'antd';
import { StyledMenuUpdateModal, MenuUpdateThumbnail, MenuUpdateFoodMallWrapper, MenuUpdateButtonGroup } from './Style';
import { getUploadContentType, isAllowedUploadFileName, UPLOAD_IMAGE_ACCEPT } from 'utils';

const { useForm } = Form;

interface MenuUpdateModalProps {
  open: boolean;
  onCancel: () => void;
  menuData?: IMenuDetailData | null;
  storeId: string;
  isFoodMallSupplyType: boolean;
  menuCategoriesSelectOptions: { label: string; value: number }[];
  refetchMenuLists: () => void;
}

const isS3Url = (url: string | null): boolean => url?.includes('https://s3.ap-northeast-2.amazonaws.com/') ?? false;

const uploadFileToS3 = async (preSignedUrl: string, file: File): Promise<void> => {
  try {
    const response = await axios.put(preSignedUrl, file, {
      headers: { 'Content-Type': getUploadContentType(file.name) }
    });
    if (response.status !== 200) throw new Error('S3 업로드 실패');
  } catch (error) {
    console.error('S3 Upload Error:', error);
    throw new Error('S3 업로드 실패');
  }
};

const getImageDimensions = (url: string): Promise<{ width: number; height: number }> => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve({ width: img.width, height: img.height });
    img.onerror = () => reject(new Error('이미지를 로드하는 데 실패하였습니다.'));
    img.src = url;
  });
};

const getMenuStatusValue = (status: TMenuStatus): number => {
  switch (status) {
    case 'ACTIVE':
      return 1;
    case 'INACTIVE':
      return 0;
    case 'SOLD_OUT':
      return 2;
    default:
      return 0;
  }
};

const MenuUpdateModal: React.FC<MenuUpdateModalProps> = ({
  open,
  onCancel,
  menuData,
  storeId,
  isFoodMallSupplyType,
  menuCategoriesSelectOptions,
  refetchMenuLists
}) => {
  const [form] = useForm();

  // 이미지 상태 통합 관리
  const [imageStates, setImageStates] = useState<{
    foodmallImageThumbnail: IImageState;
    foodmallDetailImage: IImageState;
    imageThumbnail: IImageState;
  }>({
    foodmallImageThumbnail: { url: null, file: null },
    foodmallDetailImage: { url: null, file: null },
    imageThumbnail: { url: null, file: null }
  });

  const inputRefs = {
    FOODMALL_IMAGE_THUMBNAIL: useRef<HTMLInputElement>(null),
    FOODMALL_DETAIL_IMAGE: useRef<HTMLInputElement>(null),
    IMAGE_THUMBNAIL: useRef<HTMLInputElement>(null)
  };

  const { mutateAsync: updateMenusInfoAsync } = useUpdateMenusInfo();
  const { mutateAsync: postPreSignedUrl } = usePostPreSignedUrl();

  // menuDetailData 업데이트 되면 form에 값 설정 및 이미지 상태 초기화
  useEffect(() => {
    if (menuData) {
      const initialThumbnailUrl = menuData.imageThumbnail?.url || null;
      const initialDetailUrl = menuData.detailImage?.url || null;

      setImageStates((prev) => ({
        ...prev,
        foodmallImageThumbnail: { url: initialThumbnailUrl, file: null },
        foodmallDetailImage: { url: initialDetailUrl, file: null },
        imageThumbnail: { url: initialThumbnailUrl, file: null }
      }));

      form.setFieldsValue({
        categoryId: menuData.categoryId,
        menuname: menuData.name,
        intro: menuData.intro,
        status: menuData.status,
        price: (menuData.price || 0)?.toLocaleString(),
        sellPrice: (menuData.sellPrice || 0)?.toLocaleString(),
        imageThumbnail: initialThumbnailUrl,
        detailImage: initialDetailUrl
      });
    } else {
      form.resetFields();
      setImageStates({
        foodmallImageThumbnail: { url: null, file: null },
        foodmallDetailImage: { url: null, file: null },
        imageThumbnail: { url: null, file: null }
      });
    }
  }, [menuData, form, isFoodMallSupplyType]);

  // preSignedUrl 관련 로직
  const getPreSignedUrl = useCallback(
    async (file: File, type: TImageType) => {
      if (!menuData) return;

      const serviceName = type === 'FOODMALL_DETAIL_IMAGE' ? 'store-menu-detail' : 'store-menu-thumbnail';
      const params = {
        serviceName,
        fileName: file.name,
        pathVariable: { storeId }
      };
      return await postPreSignedUrl(params);
    },
    [menuData, storeId, postPreSignedUrl]
  );

  // 이미지 업로드 및 폼 필드 업데이트 로직
  const processImageUpload = useCallback(
    async (file: File, type: TImageType, currentUrl: string | null) => {
      if (!file || !file.type.includes('image')) {
        // 파일이 없거나 이미지가 아니면 현재 이미지의 크기만 가져옴 (S3 URL일 경우)
        if (currentUrl && isS3Url(currentUrl)) {
          const dimensions = await getImageDimensions(currentUrl);
          return { url: currentUrl, width: dimensions.width, height: dimensions.height };
        }
        return { url: currentUrl, width: 0, height: 0 };
      }

      const objectUrl = URL.createObjectURL(file);
      let dimensions;
      try {
        dimensions = await getImageDimensions(objectUrl);
      } finally {
        URL.revokeObjectURL(objectUrl);
      }

      const preSignedUrlResponse = await getPreSignedUrl(file, type);
      const { preSignedUrl, uploadedFileUrl } = preSignedUrlResponse as { preSignedUrl: string; uploadedFileUrl: string };
      await uploadFileToS3(preSignedUrl, file);

      return {
        url: uploadedFileUrl,
        width: dimensions.width,
        height: dimensions.height
      };
    },
    [getPreSignedUrl]
  );

  const handleUpdateMenu = async () => {
    if (!menuData || !form) return;

    try {
      const values = await form.validateFields();
      const isImageThumbnail = isFoodMallSupplyType && !values.imageThumbnail;
      const isDetailImage = isFoodMallSupplyType && !values.detailImage;

      if (!values.menuname || !values.intro || isDetailImage || isImageThumbnail) {
        notification.error({ message: '필수 입력 항목을 확인해주세요.' });
        return;
      }

      let imageThumbnailInfo = { url: values.imageThumbnail || null, width: 0, height: 0 };
      let detailImageInfo = { url: values.detailImage || null, width: 0, height: 0 };

      // 썸네일 이미지 처리
      const thumbnailType = isFoodMallSupplyType ? 'FOODMALL_IMAGE_THUMBNAIL' : 'IMAGE_THUMBNAIL';
      const thumbnailState = isFoodMallSupplyType ? imageStates.foodmallImageThumbnail : imageStates.imageThumbnail;

      imageThumbnailInfo = await processImageUpload(thumbnailState.file as File, thumbnailType, thumbnailState.url);
      form.setFieldsValue({ imageThumbnail: imageThumbnailInfo.url });

      // 푸드몰 상세 이미지 처리
      if (isFoodMallSupplyType) {
        detailImageInfo = await processImageUpload(
          imageStates.foodmallDetailImage.file as File,
          'FOODMALL_DETAIL_IMAGE',
          imageStates.foodmallDetailImage.url
        );
        form.setFieldsValue({ detailImage: detailImageInfo.url });
      }

      const statusValue = getMenuStatusValue(values.status);

      const baseParams = {
        ...values,
        menuId: menuData.id,
        status: statusValue,
        imageThumbnail: imageThumbnailInfo.url
          ? { url: imageThumbnailInfo.url, width: imageThumbnailInfo.width, height: imageThumbnailInfo.height }
          : null
      };

      const finalParams = isFoodMallSupplyType
        ? {
            ...baseParams,
            detailImage: detailImageInfo.url
              ? { url: detailImageInfo.url, width: detailImageInfo.width, height: detailImageInfo.height }
              : null
          }
        : baseParams;

      await updateMenusInfoAsync({ sid: storeId, params: finalParams });

      notification.success({ message: '메뉴 정보가 성공적으로 수정되었습니다.' });
    } catch (error: any) {
      console.error('메뉴 수정 오류:', error);
      if (error?.errorFields) {
        notification.error({ message: error?.errorFields[0]?.errors?.[0] });
      } else {
        notification.error({ message: '메뉴 수정 실패', description: error.message || '정보 수정 중 오류가 발생했습니다.' });
      }
      return;
    }

    await refetchMenuLists();
    onCancel();
  };

  // 이미지 인풋 클릭 핸들러
  const imageUploadOnClick = (type: TImageType) => {
    inputRefs[type as keyof typeof inputRefs]?.current?.click();
  };

  // 이미지 상태 업데이트 헬퍼 함수
  const updateImageState = (type: TImageType, newUrl: string | null, newFile: File | null) => {
    setImageStates((prev) => {
      let key: keyof typeof prev;
      if (type === 'FOODMALL_IMAGE_THUMBNAIL') key = 'foodmallImageThumbnail';
      else if (type === 'FOODMALL_DETAIL_IMAGE') key = 'foodmallDetailImage';
      else key = 'imageThumbnail';

      // URL이 변경되면 폼 필드에도 반영
      form.setFieldsValue({
        [key === 'imageThumbnail' ? 'imageThumbnail' : key === 'foodmallImageThumbnail' ? 'imageThumbnail' : 'detailImage']:
          newUrl
      });

      return {
        ...prev,
        [key]: { url: newUrl, file: newFile }
      };
    });
  };

  // 파일 선택/변경 핸들러
  const handleImageChange =
    (type: TImageType) =>
    ({ target }: React.ChangeEvent<HTMLInputElement>) => {
      const file = target.files?.[0];
      if (file) {
        if (!isAllowedUploadFileName(file.name)) {
          notification.error({ message: 'jpg, jpeg, png, gif 형식의 이미지만 업로드할 수 있습니다.' });
          return;
        }
        const objectUrl = URL.createObjectURL(file);
        updateImageState(type, objectUrl, file);
      }
    };

  // 드래그 앤 드롭 핸들러
  const handleImageDrop = (type: TImageType) => async (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    const file = event.dataTransfer.files?.[0];

    if (file && file.type.includes('image')) {
      if (!isAllowedUploadFileName(file.name)) {
        notification.error({ message: 'jpg, jpeg, png, gif 형식의 이미지만 업로드할 수 있습니다.' });
        return;
      }
      const objectUrl = URL.createObjectURL(file);
      updateImageState(type, objectUrl, file);
    }
  };

  // 이미지 삭제 핸들러
  const handleImageRemove = (type: TImageType) => () => {
    updateImageState(type, null, null);
  };

  // 렌더링에 사용할 이미지 상태 결정 (isFoodMallSupplyType에 따라)
  const currentImageStates = useMemo(() => {
    if (isFoodMallSupplyType) {
      return {
        thumbnail: imageStates.foodmallImageThumbnail,
        detail: imageStates.foodmallDetailImage
      };
    }
    return {
      thumbnail: imageStates.imageThumbnail,
      detail: { url: null, file: null }
    };
  }, [isFoodMallSupplyType, imageStates]);

  const renderImageSection = (
    type: TImageType,
    label: string,
    imageState: IImageState,
    inputRef: React.RefObject<HTMLInputElement>
  ) => {
    const formItemName = type === 'FOODMALL_DETAIL_IMAGE' ? 'detailImage' : 'imageThumbnail';
    const isFoodmallSpecific = type.startsWith('FOODMALL');
    const rules = isFoodMallSupplyType ?[ { required: true, message: `${label}를 입력해주세요.` }] : null;

    return (
      <Form.Item
        name={formItemName}
        label={label}
        wrapperCol={{ flex: 'none' }}
        rules={rules}
        className="menu-update-thumbnail-form-item"
        getValueFromEvent={(e) => e} // Antd Form이 이미지 URL을 제대로 추적하도록 설정
      >
        <div className="image-button-flex-group">
          <MenuUpdateThumbnail
            role="presentation"
            onPointerUp={() => imageUploadOnClick(type)}
            onDrop={handleImageDrop(type)}
            onDragOver={(e) => e.preventDefault()}
          >
            <input
              type="file"
              accept={UPLOAD_IMAGE_ACCEPT}
              ref={inputRef}
              onChange={handleImageChange(type)}
              style={{ display: 'none' }}
            />
            {imageState.url ? (
              <img alt="previewImage" className="preview-image" draggable={false} src={imageState.url} />
            ) : (
              <div className="non-image-description">
                <div style={{ fontSize: '16px', fontWeight: 500, marginBottom: '8px' }}>이미지를 등록해주세요</div>
                <div style={{ fontSize: '14px', color: '#666' }}>드래그 앤 드랍 또는 클릭</div>
                <div className="image-guide-description">(권장 사이즈: 400 x 300)</div>
              </div>
            )}
          </MenuUpdateThumbnail>
          <MenuUpdateButtonGroup>
            <Button type="primary" onClick={() => imageUploadOnClick(type)}>
              이미지 등록/교체
            </Button>
            {!isFoodmallSpecific && (
              <Button type="primary" onClick={handleImageRemove(type)}>
                등록 이미지 삭제
              </Button>
            )}
            {(!isFoodmallSpecific || type === 'FOODMALL_IMAGE_THUMBNAIL') && (
              <div className="image-guide-description">* 1:1 정방형 이미지에 최적화 되어 있습니다.</div>
            )}
          </MenuUpdateButtonGroup>
        </div>
      </Form.Item>
    );
  };

  return (
    <StyledMenuUpdateModal
      title="메뉴 수정"
      open={open}
      onCancel={onCancel}
      footer={
        <>
          <Button onClick={onCancel}>닫기</Button>
          <Button type="primary" onClick={handleUpdateMenu}>
            수정하기
          </Button>
        </>
      }
    >
      <div className="menu-setting-section">
        <div className="section-title">메뉴 설정</div>
        <Form
          form={form}
          layout="horizontal"
          labelCol={{ flex: '110px' }}
          labelAlign="left"
          labelWrap
          wrapperCol={{ flex: 1 }}
          colon={false}
          name="menu_update_form"
        >
          <Form.Item name="categoryId" label="분류" rules={[{ required: true, message: '분류를 선택해주세요.' }]}>
            <Select options={menuCategoriesSelectOptions} />
          </Form.Item>
          <Form.Item name="menuname" label="메뉴명" rules={[{ required: true, message: '메뉴명을 입력해주세요.' }]}>
            <Input maxLength={50} />
          </Form.Item>

          {isFoodMallSupplyType ? (
            <MenuUpdateFoodMallWrapper>
              {renderImageSection(
                'FOODMALL_IMAGE_THUMBNAIL',
                '메뉴 이미지',
                currentImageStates.thumbnail,
                inputRefs.FOODMALL_IMAGE_THUMBNAIL
              )}
              {renderImageSection(
                'FOODMALL_DETAIL_IMAGE',
                '상세 이미지',
                currentImageStates.detail,
                inputRefs.FOODMALL_DETAIL_IMAGE
              )}
            </MenuUpdateFoodMallWrapper>
          ) : (
            renderImageSection('IMAGE_THUMBNAIL', '메뉴 이미지', currentImageStates.thumbnail, inputRefs.IMAGE_THUMBNAIL)
          )}

          <Form.Item
            name="intro"
            label="메뉴 소개"
            rules={[{ required: true, message: '메뉴 소개를 입력해주세요.' }]}
            className="menu-update-intro-form-item"
          >
            <Input maxLength={200} />
          </Form.Item>
          <Form.Item name="status" label="메뉴 상태" rules={[{ required: true, message: '메뉴 상태를 선택해주세요.' }]}>
            <Select options={menuStatusItems} />
          </Form.Item>

          <div className="price-setting-section">
            <div className="section-title">금액 설정</div>
            <Form.Item name="price" label="가격" rules={[{ required: true }]}>
              <Input disabled />
            </Form.Item>
            {isFoodMallSupplyType ? (
              <Form.Item name="sellPrice" label="판매 가격" rules={[{ required: true }]}>
                <Input disabled />
              </Form.Item>
            ) : null}
            <div className="price-setting-description">* 판매금액은 하단의 1:1문의 또는 담당자를 통하여 수정해주세요.</div>
          </div>
        </Form>
      </div>
    </StyledMenuUpdateModal>
  );
};

export default MenuUpdateModal;
